import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { getUsers, blockUser, unblockUser, deleteUsers } from '../api/admin';
import { useCountry } from '../context/CountryContext';
import Badge from '../components/Badge';
import Pagination from '../components/Pagination';
import Modal from '../components/Modal';
import ConfirmAccountDeletionModal, { referenceLabelFor } from '../components/ConfirmAccountDeletionModal';
import UserDetailModal from '../components/UserDetailModal';
import styles from './Users.module.css';

const ROLES = ['', 'private_person', 'vehicle_owner', 'insurance_agency', 'admin', 'driver', 'bodaboda', 'hotel_host', 'event_host'];
const ROLE_LABELS = {
  '': 'All Roles',
  private_person: 'Private Person',
  vehicle_owner: 'Vehicle Owner',
  insurance_agency: 'Insurance Agency',
  admin: 'Admin',
  // KE provider account types (Kenya Registration Plan)
  driver: 'Driver (KE)',
  bodaboda: 'Boda-Boda (KE)',
  hotel_host: 'Hotel Host (KE)',
  event_host: 'Event Host (KE)',
};

const SORT_OPTIONS = [
  { value: 'created_at_desc', label: 'Joined (Newest)' },
  { value: 'created_at_asc', label: 'Joined (Oldest)' },
  { value: 'full_name_asc', label: 'Name (A-Z)' },
  { value: 'full_name_desc', label: 'Name (Z-A)' },
];

function fmt(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

export default function Users() {
  const queryClient = useQueryClient();
  const { country } = useCountry();
  const { filter: urlFilter } = useParams();

  // Map URL param to is_active filter
  const urlIsActive = urlFilter === 'active' ? 'true'
    : urlFilter === 'blocked' ? 'false'
    : '';

  const [page, setPage] = useState(1);
  const [role, setRole] = useState('');
  const [isActive, setIsActive] = useState(urlIsActive);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [sortBy, setSortBy] = useState('created_at_desc');
  const [blockModal, setBlockModal] = useState(null);
  const [blockReason, setBlockReason] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  // Accounts snapshot for the deletion dialog (null = closed).
  const [deleteAccounts, setDeleteAccounts] = useState(null);
  const [deleteError, setDeleteError] = useState('');
  // Every row seen so far, so a selection made on another page still
  // resolves to its customer number in the deletion dialog.
  const seenUsersRef = useRef(new Map());
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [detailUserId, setDetailUserId] = useState(null);

  // Sync is_active filter when URL changes
  useEffect(() => {
    setIsActive(urlIsActive);
    setPage(1);
    setSelectedIds(new Set());
  }, [urlFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const params = {
    page,
    limit: 20,
    ...(role && { role }),
    ...(isActive !== '' && { is_active: isActive }),
    ...(search && { search }),
    // 'NONE' means this admin currently has no active country to show (e.g.
    // their only granted country was just paused) -- sending it as a literal
    // filter would match zero real rows instead of falling back sensibly.
    ...(country && country !== 'ALL' && country !== 'NONE' && { country }),
  };

  const { data, isLoading, isError } = useQuery({
    queryKey: ['users', params],
    queryFn: () => getUsers(params).then((r) => r.data),
    keepPreviousData: true,
  });

  const blockMutation = useMutation({
    mutationFn: ({ id, reason }) => blockUser(id, reason),
    onSuccess: () => {
      toast.success('User blocked');
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setBlockModal(null);
      setBlockReason('');
    },
    onError: (err) => toast.error(err.response?.data?.error || 'Failed to block user'),
  });

  const unblockMutation = useMutation({
    mutationFn: (id) => unblockUser(id),
    onSuccess: () => {
      toast.success('User unblocked');
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (err) => toast.error(err.response?.data?.error || 'Failed to unblock user'),
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: ({ ids, confirm }) => deleteUsers(ids, confirm),
    onSuccess: (res) => {
      toast.success(res.data?.message || 'Account deleted');
      setSelectedIds(new Set());
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setDeleteAccounts(null);
    },
    onError: (err) => setDeleteError(err.response?.data?.error || 'The account could not be deleted.'),
  });

  useEffect(() => {
    (data?.users || []).forEach((u) => seenUsersRef.current.set(u.id, u));
  }, [data]);

  function openDeleteDialog() {
    const accounts = [...selectedIds].map((id) => seenUsersRef.current.get(id)).filter(Boolean);
    if (accounts.length !== selectedIds.size) {
      toast.error('Some selected accounts are no longer in the list. Please reselect them.');
      return;
    }
    setDeleteError('');
    setDeleteAccounts(accounts);
  }

  function toggleSelect(id) {
    setSelectedIds((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  }

  function toggleSelectAll() {
    const all = data?.users || [];
    setSelectedIds(selectedIds.size === all.length ? new Set() : new Set(all.map((u) => u.id)));
  }

  function handleSearch(e) {
    e.preventDefault();
    setSearch(searchInput.trim());
    setPage(1);
  }

  function handleClearSearch() {
    setSearchInput('');
    setSearch('');
    setPage(1);
  }

  function handleFilter(setter, val) {
    setter(val);
    setPage(1);
  }

  function handleBlockSubmit(e) {
    e.preventDefault();
    blockMutation.mutate({ id: blockModal.id, reason: blockReason });
  }

  function handleUnblock(user) {
    if (window.confirm(`Unblock ${user.full_name || user.email}?`)) {
      unblockMutation.mutate(user.id);
    }
  }

  // Sort users client-side for now
  let usersToShow = data?.users || [];
  if (sortBy === 'created_at_desc') {
    usersToShow.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  } else if (sortBy === 'created_at_asc') {
    usersToShow.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  } else if (sortBy === 'full_name_asc') {
    usersToShow.sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''));
  } else if (sortBy === 'full_name_desc') {
    usersToShow.sort((a, b) => (b.full_name || '').localeCompare(a.full_name || ''));
  }

  const pagination = data?.pagination;
  const allSelected = usersToShow.length > 0 && selectedIds.size === usersToShow.length;

  return (
    <div>
      <div className={styles.header}>
        <h1 className={styles.pageTitle}>
          Users
          {urlFilter && urlFilter !== 'all' && (
            <span style={{ marginLeft: '10px', fontSize: '13px', fontWeight: '500', padding: '3px 10px', borderRadius: '20px', background: '#eef2ff', color: '#6366f1' }}>
              {urlFilter.charAt(0).toUpperCase() + urlFilter.slice(1)}
            </span>
          )}
        </h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {pagination && <span className={styles.total}>{pagination.total} total</span>}
          {selectedIds.size > 0 && (
            <button
              onClick={openDeleteDialog}
              disabled={bulkDeleteMutation.isPending}
              style={{ padding: '6px 14px', background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '500' }}
            >
              🗑️ Delete {selectedIds.size} selected
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className={styles.filterBar}>
        <form onSubmit={handleSearch} className={styles.searchForm}>
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Search by name, email, or Customer #…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          <button type="submit" className={styles.searchBtn}>Search</button>
          {search && (
            <button type="button" className={styles.clearBtn} onClick={handleClearSearch}>
              Clear
            </button>
          )}
        </form>

        <select
          className={styles.select}
          value={role}
          onChange={(e) => handleFilter(setRole, e.target.value)}
        >
          {ROLES.map((r) => (
            <option key={r} value={r}>{ROLE_LABELS[r]}</option>
          ))}
        </select>

        <select
          className={styles.select}
          value={isActive}
          onChange={(e) => handleFilter(setIsActive, e.target.value)}
        >
          <option value="">All Status</option>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </select>

        <select
          className={styles.select}
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
        >
          {SORT_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      {isLoading && <p className={styles.msg}>Loading…</p>}
      {isError && <p className={styles.msgError}>Failed to load users.</p>}

      {data && (
        <>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th><input type="checkbox" checked={allSelected} onChange={toggleSelectAll} aria-label="Select all" /></th>
                  <th>Reference #</th>
                  <th>Name</th>
                  <th>Country</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Identity</th>
                  <th>Document Expiry</th>
                  <th>Joined</th>
                  <th>Last Login</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {usersToShow.length === 0 && (
                  <tr>
                    <td colSpan={12} className={styles.empty}>No users found.</td>
                  </tr>
                )}
                {usersToShow.map((u) => (
                  <tr key={u.id} style={{ background: selectedIds.has(u.id) ? '#eef2ff' : undefined }}>
                    <td><input type="checkbox" checked={selectedIds.has(u.id)} onChange={() => toggleSelect(u.id)} aria-label="Select row" /></td>
                    <td style={{ fontSize: '12px', color: '#374151' }} title={u.id}>{u.customer_number ? `${referenceLabelFor(u.user_role)} #${u.customer_number}` : <span style={{ fontFamily: 'monospace', color: '#9ca3af' }}>{u.id.slice(0, 8)}...</span>}</td>
                    <td className={styles.name}>{u.full_name || '—'}</td>
                    <td>{u.country_code || <span style={{ color: '#9ca3af' }}>No country</span>}</td>
                    <td>{u.email}</td>
                    <td><Badge value={u.user_role} label={ROLE_LABELS[u.user_role] || u.user_role} /></td>
                    <td>
                      <Badge
                        value={u.is_active ? 'active' : 'inactive'}
                        label={u.is_active ? 'Active' : 'Inactive'}
                      />
                    </td>
                    <td>
                      <Badge
                        value={String(u.identity_verified)}
                        label={u.identity_verified ? 'Verified' : 'Unverified'}
                      />
                    </td>
                    <td>
                      {u.earliest_expiry ? (() => {
                        const today = new Date();
                        const exp = new Date(u.earliest_expiry);
                        const threeMonths = new Date(); threeMonths.setMonth(today.getMonth() + 3);
                        const color = exp < today ? '#dc2626' : exp <= threeMonths ? '#d97706' : '#16a34a';
                        const icon = exp < today ? '🔴' : exp <= threeMonths ? '⚠️' : '✅';
                        return (
                          <span title={fmt(u.earliest_expiry)} style={{ color, fontSize: '13px', fontWeight: 500 }}>
                            {icon} {fmt(u.earliest_expiry)}
                          </span>
                        );
                      })() : u.user_role === 'private_person' || u.user_role === 'vehicle_owner' ? (
                        <span style={{ color: '#9ca3af', fontSize: '13px' }}>Not set</span>
                      ) : '—'}
                    </td>
                    <td>{fmt(u.created_at)}</td>
                    <td>{fmt(u.last_login)}</td>
                    <td>
                      <div style={{ position: 'relative' }}>
                        <button
                          style={{ padding: '5px 12px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '5px', cursor: 'pointer', fontSize: '12px' }}
                          onClick={() => setOpenDropdownId(openDropdownId === u.id ? null : u.id)}
                        >
                          ⋯ Actions
                        </button>
                        {openDropdownId === u.id && (
                          <div style={{
                            position: 'absolute',
                            right: 0,
                            top: '100%',
                            marginTop: '4px',
                            background: '#fff',
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                            zIndex: 10,
                            minWidth: '150px'
                          }} onClick={(e) => e.stopPropagation()}>
                            <button
                              style={{ display: 'block', width: '100%', padding: '10px 16px', textAlign: 'left', border: 'none', background: 'none', cursor: 'pointer', fontSize: '13px', color: '#111827' }}
                              onClick={() => { setOpenDropdownId(null); setDetailUserId(u.id); }}
                            >
                              View details &amp; documents
                            </button>
                            {u.user_role !== 'admin' && (
                              <>
                                {u.is_active ? (
                                  <button
                                    style={{
                                      display: 'block',
                                      width: '100%',
                                      padding: '10px 16px',
                                      textAlign: 'left',
                                      border: 'none',
                                      background: 'none',
                                      cursor: 'pointer',
                                      fontSize: '13px',
                                      color: '#dc2626'
                                    }}
                                    onClick={() => {
                                      setOpenDropdownId(null);
                                      setBlockModal(u);
                                      setBlockReason('');
                                    }}
                                  >
                                    Block User
                                  </button>
                                ) : (
                                  <button
                                    style={{
                                      display: 'block',
                                      width: '100%',
                                      padding: '10px 16px',
                                      textAlign: 'left',
                                      border: 'none',
                                      background: 'none',
                                      cursor: 'pointer',
                                      fontSize: '13px',
                                      color: '#16a34a'
                                    }}
                                    onClick={() => {
                                      setOpenDropdownId(null);
                                      handleUnblock(u);
                                    }}
                                    disabled={unblockMutation.isPending}
                                  >
                                    Unblock User
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pagination
            page={pagination?.page || 1}
            pages={pagination?.pages || 1}
            onPage={setPage}
          />
        </>
      )}

      {/* Block reason modal */}
      {blockModal && (
        <Modal title="Block User" onClose={() => { setBlockModal(null); setBlockReason(''); }}>
          <p className={styles.blockInfo}>
            Blocking <strong>{blockModal.full_name || blockModal.email}</strong>.
            Provide an optional reason.
          </p>
          <form onSubmit={handleBlockSubmit}>
            <textarea
              className={styles.textarea}
              placeholder="Reason (optional)…"
              value={blockReason}
              onChange={(e) => setBlockReason(e.target.value)}
              rows={3}
            />
            <div className={styles.modalActions}>
              <button
                type="button"
                className={styles.cancelBtn}
                onClick={() => { setBlockModal(null); setBlockReason(''); }}
              >
                Cancel
              </button>
              <button
                type="submit"
                className={styles.blockBtn}
                disabled={blockMutation.isPending}
              >
                {blockMutation.isPending ? 'Blocking…' : 'Confirm Block'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {detailUserId && (
        <UserDetailModal
          userId={detailUserId}
          onClose={() => setDetailUserId(null)}
          onChanged={() => queryClient.invalidateQueries({ queryKey: ['users'] })}
          onDeleted={() => queryClient.invalidateQueries({ queryKey: ['users'] })}
        />
      )}

      {deleteAccounts && (
        <ConfirmAccountDeletionModal
          accounts={deleteAccounts}
          pending={bulkDeleteMutation.isPending}
          serverError={deleteError}
          onCancel={() => setDeleteAccounts(null)}
          onConfirm={(confirm) => {
            setDeleteError('');
            bulkDeleteMutation.mutate({ ids: deleteAccounts.map((u) => u.id), confirm });
          }}
        />
      )}
    </div>
  );
}
